import { useRef } from 'react';

import { useGetETFDetail } from '#/api/generated/etf/etf';
import { CondensedTickerSummary } from '#/components/detail/CondensedTickerSummary';
import { DetailNotFoundState } from '#/components/detail/DetailNotFoundState';
import { DetailSectionNav } from '#/components/detail/DetailSectionNav';
import { DetailSkeleton } from '#/components/detail/DetailSkeleton';
import { ETFPerformanceSection } from '#/components/detail/ETFPerformanceSection';
import { ETFSummarySection } from '#/components/detail/ETFSummarySection';
import { LearnMoreSection } from '#/components/detail/LearnMoreSection';
import { PortfolioSection } from '#/components/detail/PortfolioSection';
import { RelatedSection } from '#/components/detail/RelatedSection';
import { DETAIL_SECTION_NAV } from '#/components/detail/sectionNav';
import { StaticLearnMoreResourcesSection } from '#/components/detail/StaticLearnMoreResourcesSection';
import { TopHoldingsSection } from '#/components/detail/TopHoldingsSection';
import type { DetailViewProps } from '#/components/detail/types';
import { ContentLayout } from '#/components/layout/ContentLayout';
import { Breadcrumbs } from '#/components/ui/Breadcrumbs';
import { ErrorBoundary } from '#/components/ui/ErrorBoundary';
import { QueryBoundary } from '#/components/ui/QueryBoundary';
import { useETFDetailContent } from '#/content/etf-detail';
import { useDefinitionDrawer } from '#/context/DefinitionDrawerContext';
import { useBreadcrumbs } from '#/hooks/useBreadcrumbs';
import { useMeasureOnCommit } from '#/hooks/usePerformanceMeasure';
import { isSuccessResponse } from '#/lib/api-client';
import { MDC_MEASURE } from '#/lib/performance';
import { getEquityETFPriceChangeDisplay } from '#/utils/priceChange';

const SECTION_NAV = DETAIL_SECTION_NAV['etfs'];

export function ETFDetail({ category, id }: DetailViewProps) {
  const breadcrumbs = useBreadcrumbs();
  const contentRef = useRef<HTMLDivElement>(null);
  const priceSummaryRef = useRef<HTMLDivElement>(null);

  const query = useGetETFDetail(id);
  const { content, definitions } = useETFDetailContent();

  // Publish this page's glossary terms for tooltips and the drawer.
  useDefinitionDrawer(definitions);

  // Closes `mdc:data-visible` on the commit that swaps the loading state for
  // real detail data.
  useMeasureOnCommit(MDC_MEASURE.dataVisible, isSuccessResponse(query.data));

  return (
    <QueryBoundary
      query={query}
      loading={<DetailSkeleton category="etfs" symbol={id} />}
      notFound={(error) => <DetailNotFoundState symbol={id} error={error} />}
    >
      {(response) => {
        const { percentChange, valueChange, periodLabel } =
          getEquityETFPriceChangeDisplay(response.data.overview);

        return (
          <ContentLayout>
            <DetailSectionNav
              sections={SECTION_NAV}
              contentRef={contentRef}
              priceSummaryRef={priceSummaryRef}
              jumpTrack={{
                track: 'section_nav_jump',
                context: 'detail_section_nav',
              }}
              condensedSummary={
                <CondensedTickerSummary
                  overview={response.data.overview}
                  percentChange={percentChange}
                  valueChange={valueChange}
                  changePeriodLabel={periodLabel}
                />
              }
            />

            <div className="col-content flow flow-60" ref={contentRef}>
              <Breadcrumbs
                items={breadcrumbs}
                dataTrack="breadcrumb_link"
                dataTrackContext="breadcrumb"
              />

              <ErrorBoundary>
                <ETFSummarySection
                  data={response.data.overview}
                  id={id}
                  priceSummaryRef={priceSummaryRef}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <ETFPerformanceSection
                  symbol={id}
                  keyStats={response.data.key_stats}
                  price={response.data.price}
                  risk={response.data.risk}
                  categoryCode={response.data.related?.category_code}
                  categoryName={response.data.related?.category}
                  description="Prices are refreshed every 15 minutes. To view historical returns and performance data for ETFs, visit the <a href='https://tools.finra.org/fund_analyzer/' target='_blank' rel='noopener noreferrer'>FINRA Fund Analyzer</a>."
                  date={response.data.overview?.last_refreshed_timestamp}
                  priceIntro={content.priceIntro}
                  riskIntro={content.riskIntro}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <PortfolioSection
                  data={response.data.portfolio}
                  description={content.portfolioIntro}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <TopHoldingsSection
                  data={response.data.top_holdings}
                  description={content.topHoldingsIntro}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <RelatedSection
                  category={category}
                  fundCategory={response.data.related?.category}
                  data={response.data.related}
                />
              </ErrorBoundary>

              {content.learnMoreArticles && (
                <ErrorBoundary>
                  <LearnMoreSection
                    title="Learn More About ETFs"
                    articles={content.learnMoreArticles}
                    articleTrack="learn_more_article"
                    articleTrackContext="learn_more"
                  />
                </ErrorBoundary>
              )}

              <StaticLearnMoreResourcesSection
                id={content.learnMoreArticles ? undefined : 'learn-more'}
              />
            </div>
          </ContentLayout>
        );
      }}
    </QueryBoundary>
  );
}
